import { useState, useRef, useEffect, useMemo } from "react";
import { DatePicker, DatesRangeValue } from "@mantine/dates";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faPlus,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import { subDays, format } from "date-fns";
import { DATE_ONLY_FORMAT } from "shared/src/utils/date-utils";

import styles from "./date-range-filter.module.scss";

export type Preset = {
  label: string;
  days: number;
};

export const PRESETS: Preset[] = [
  { label: "Last 3 days", days: 3 },
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
];

const toDateString = (d: Date): string => format(d, DATE_ONLY_FORMAT);

export const getPresetRange = (days: number): [string, string] => {
  const today = new Date();
  return [toDateString(subDays(today, days)), toDateString(today)];
};

const toDateValue = (val: string | null): Date | null => {
  if (!val) return null;
  const [year, month, day] = val.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toStringValue = (val: DatesRangeValue[number]): string | null => {
  if (!val) return null;
  if (val instanceof Date) return format(val, DATE_ONLY_FORMAT);
  return val;
};

const formatTriggerDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return format(new Date(year, month - 1, day), "MMM d, yyyy");
};

type DateRangeFilterProps = {
  dateRange: [string | null, string | null];
  onChange: (range: [string | null, string | null]) => void;
  onClear: () => void;
};

const DateRangeFilter = ({
  dateRange,
  onChange,
  onClear,
}: DateRangeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingRange, setPendingRange] = useState<DatesRangeValue>([
    null,
    null,
  ]);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasSelection = dateRange[0] !== null && dateRange[1] !== null;

  const activePresetIndex = useMemo(() => {
    if (!hasSelection) return -1;
    return PRESETS.findIndex((preset) => {
      const [start, end] = getPresetRange(preset.days);
      return start === dateRange[0] && end === dateRange[1];
    });
  }, [dateRange, hasSelection]);

  const pickerValue: DatesRangeValue = useMemo(
    () =>
      isOpen
        ? pendingRange
        : [toDateValue(dateRange[0]), toDateValue(dateRange[1])],
    [isOpen, pendingRange, dateRange],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setPendingRange([toDateValue(dateRange[0]), toDateValue(dateRange[1])]);
    setIsOpen(true);
  };

  const handleDateChange = (value: DatesRangeValue) => {
    setPendingRange(value);
    const from = toStringValue(value[0]);
    const to = toStringValue(value[1]);
    if (from && to) {
      onChange([from, to]);
      setIsOpen(false);
    }
  };

  const handlePresetClick = (preset: Preset) => {
    const [from, to] = getPresetRange(preset.days);
    onChange([from, to]);
    setPendingRange([toDateValue(from), toDateValue(to)]);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear();
    setIsOpen(false);
  };

  const dateRangeLabel = hasSelection
    ? `${formatTriggerDate(dateRange[0]!)} - ${formatTriggerDate(dateRange[1]!)}`
    : null;

  const triggerLabel = dateRangeLabel ?? "Status Changed Date";

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={`${styles.trigger} ${hasSelection ? styles.active : ""}`}
        onClick={handleOpen}
        type="button"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={hasSelection ? faCalendar : faPlus} />
        {triggerLabel}
        {hasSelection && (
          <FontAwesomeIcon
            icon={faXmark}
            className={styles.clearIcon}
            onClick={handleClear}
            data-testid="date-range-clear"
          />
        )}
      </button>
      {isOpen && (
        <div className={styles.dropdown}>
          {dateRangeLabel && (
            <div className={styles.dropdownHeader}>{dateRangeLabel}</div>
          )}
          <div className={styles.dropdownBody}>
            <div className={styles.presetSidebar}>
              {PRESETS.map((preset, index) => (
                <button
                  key={preset.label}
                  type="button"
                  className={`${styles.presetItem} ${activePresetIndex === index ? styles.presetActive : ""}`}
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className={styles.calendarPanel}>
              <DatePicker
                type="range"
                allowSingleDateInRange
                value={pickerValue}
                onChange={handleDateChange}
                maxDate={new Date()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
